import { Box, Button, Flex, HStack, Text } from '@chakra-ui/react';
import { isEmpty } from 'lodash-es';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';
import { routes } from '@lon/login/configs';
import {
  PasswordInput,
  PasswordRequirement,
  RequiredNotification,
  TextInput,
} from '@lon/shared/components';
import { Icon } from '@lon/shared/components';
import { useMediaQuery } from '@lon/shared/hooks';
import { generatePassword } from '@lon/shared/utils';
import { types, utils } from './duck';

const Form: FC<types.FormTypes> = ({ loading, isPreview }) => {
  const { t } = useTranslation();
  const [isMobile] = useMediaQuery('(max-width: 767px)');
  const passwordRef = useRef<types.ShowPasswordMethod>(null);
  const repeatRef = useRef<types.ShowPasswordMethod>(null);
  const [passwordErrors, setPasswordErrors] = useState<number[] | null>(null);

  const form = useFormContext();

  const isDirty =
    form.formState.isDirty || !isEmpty(form.formState.dirtyFields);

  const formValuePassword = form.watch('password');

  useEffect(() => {
    const passwordErrors = utils.validatePassword(
      formValuePassword,
      !!isPreview
    );
    formValuePassword && setPasswordErrors(passwordErrors);
  }, [formValuePassword]);

  const handleClickGenerate = () => {
    const newPassword = generatePassword(isPreview ? 10 : 16);

    form.setValue('password', newPassword, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue('repeatedPassword', newPassword, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (passwordRef.current?.showPassword) {
      passwordRef.current?.showPassword();
    }
    if (repeatRef.current?.showPassword) {
      repeatRef.current?.showPassword();
    }
    form.trigger('password');
    form.trigger('repeatedPassword');
  };

  const handleReset = () => {
    form.resetField('username');
    form.resetField('password');
    form.resetField('repeatedPassword');

    if (isPreview) {
      form.resetField('email');
      form.resetField('lastName');
      form.resetField('firstName');
    }
  };

  return (
    <Flex direction="column" p={8} gap={8}>
      <Flex direction="column" gap={4}>
        <Text variant="n1">{t('register.title')}</Text>
        <Text variant="n3">{t('register.subtitle')}</Text>
      </Flex>
      <Flex direction={{ base: 'column', md: 'row' }} gap={8}>
        <Flex direction="column" gap={6}>
          <Flex
            direction="column"
            gap={6}
            backgroundColor="secondary.50"
            borderRadius="6px"
            p={6}
          >
            <Text variant="n1">{t('register.note')}</Text>
            <Text variant="n3">{t('register.noteDescription')}</Text>
          </Flex>
          <Flex
            direction="column"
            backgroundColor="secondary.50"
            borderRadius="6px"
            py={6}
          >
            <Text fontWeight={700} mb={4} ml={6}>
              {t('register.passwordRequirements')}
            </Text>
            {[1, isPreview ? 10 : 8, 3, 4, 5, 9].map((requirement) => (
              <PasswordRequirement
                key={requirement}
                requirement={requirement}
                type={
                  !formValuePassword
                    ? 'default'
                    : passwordErrors?.includes(requirement)
                    ? 'error'
                    : 'success'
                }
              />
            ))}
          </Flex>
        </Flex>
        <Flex
          direction="column"
          minW={{ base: 'auto', md: '300px', lg: '370px' }}
          gap={4}
        >
          <RequiredNotification />

          <Flex direction="column" gap={4} mb={6}>
            <TextInput
              isRequired
              formLabelProps={{
                color: 'primary.800 !important',
              }}
              name="username"
              label={t('register.username')}
              placeholder={t('register.usernamePlaceholder')}
              isDisabled={loading}
            />
            <TextInput
              isRequired
              formLabelProps={{
                color: 'primary.800 !important',
              }}
              name="firstName"
              label={t('register.firstNameLabel')}
              placeholder={t('register.firstNamePlaceholder')}
              autoComplete="off"
              isDisabled={loading}
            />
            <TextInput
              isRequired
              formLabelProps={{
                color: 'primary.800 !important',
              }}
              name="lastName"
              label={t('register.lastNameLabel')}
              placeholder={t('register.lastNamePlaceholder')}
              autoComplete="off"
              isDisabled={loading}
            />
            <TextInput
              isRequired
              formLabelProps={{
                color: 'primary.800 !important',
              }}
              name="email"
              label={t('register.email')}
              placeholder={t('register.emailPlaceholder')}
              isDisabled={loading}
            />

            <Box position="relative">
              <PasswordInput
                ref={passwordRef}
                label={t('register.passwordLabel')}
                formLabelProps={{
                  color: 'primary.800 !important',
                }}
                name="password"
                isRequired
                placeholder={
                  isMobile
                    ? t('register.passwordPlaceholderShort')
                    : t('register.passwordPlaceholder')
                }
                autoComplete="new-password"
                isDisabled={loading}
              />
              <Button
                size="sm"
                variant="ghost"
                position="absolute"
                top="-4px"
                right={0}
                p={0}
                textDecoration="underline"
                onClick={handleClickGenerate}
              >
                {t('register.generate')}
              </Button>
            </Box>
            <PasswordInput
              ref={repeatRef}
              label={t('register.confirmPasswordLabel')}
              formLabelProps={{
                color: 'primary.800 !important',
              }}
              name="repeatedPassword"
              isRequired
              placeholder={t('register.confirmPasswordPlaceholder')}
              autoComplete="new-password"
              isDisabled={loading}
            />
          </Flex>
          <HStack justify="space-between">
            <Button
              width="min-content"
              size="md"
              variant="outline"
              mr={10}
              onClick={handleReset}
              isDisabled={!isDirty || loading}
            >
              {t('register.reset')}
            </Button>
            <Button
              leftIcon={<Icon name="check-simple-outlined" size="lg" />}
              type="submit"
              size="md"
              mt={{ xs: 9, sm: '48px', md: 0 }}
              mb={{ xs: 3, sm: 4, md: 0 }}
              variant="solid"
              isLoading={loading}
              isDisabled={!isDirty}
            >
              {t('register.submit')}
            </Button>
          </HStack>
          <Flex mt={3} gap={1.5} direction="row" justify="flex-end">
            <Text color="primary.800" variant="s2">
              {t('register.haveAnAccount')}
            </Text>
            <Text
              to={generatePath(routes.login.preview, { urlCode: undefined })}
              as={Link}
              variant="s2"
              color="primary.800"
              textDecoration="underline"
              _hover={{ textDecoration: 'none' }}
            >
              {t('register.logIn')}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Form;
